import React, { useState } from 'react';
import { Close } from '@mui/icons-material';
import { Box, IconButton, Modal, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

function ClientDetailsModal({ isOpen, onClose, client }) {
  const { t } = useTranslation('registry');
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  if (!client) return null;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDate = date => (date ? format(new Date(date), 'dd/MM/yyyy HH:mm') : t('commerce.modal.noData'));

  return (
    <Modal onClose={onClose} open={isOpen}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          left: '50%',
          maxHeight: '90vh',
          maxWidth: 900,
          outline: 'none',
          overflowY: 'auto',
          p: 0,
          position: 'absolute',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            p: 2
          }}
        >
          <Typography variant="h6">
            {t('commerce.clientName')}: {client.name}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ display: 'flex', height: 500 }}>
          {/* Left Tabs */}
          <Box sx={{ borderColor: 'divider', borderRight: 1, width: 200 }}>
            <Tabs
              aria-label="Vertical tabs example"
              onChange={handleTabChange}
              orientation="vertical"
              sx={{ borderColor: 'divider', borderRight: 1 }}
              value={activeTab}
              variant="scrollable"
            >
              <Tab label={t('commerce.modal.general')} />
              <Tab label={t('commerce.modal.commercial')} />
            </Tabs>
          </Box>

          {/* Right Panel */}
          <Box sx={{ flexGrow: 1, p: 3 }}>
            {activeTab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
                  {/* Column 1 */}
                  <Box sx={{ flex: 1 }}>
                    <Typography color="textSecondary" gutterBottom variant="subtitle2">
                      {t('commerce.modal.category')}:{' '}
                      <Typography color="textPrimary" component="span" variant="body2">
                        {client.category || t('commerce.modal.noData')}
                      </Typography>
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                        {t('commerce.modal.businessContactDetails')}:
                      </Typography>
                      <Typography variant="body2">{`${client.bc_first_name} ${client.bc_last_name}`}</Typography>
                      <Typography variant="body2">{client.bc_email}</Typography>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                        {t('commerce.modal.operationalContact')}:
                      </Typography>
                      {client.bc_is_oc ? (
                        <>
                          <Typography variant="body2">{`${client.bc_first_name} ${client.bc_last_name}`}</Typography>
                          <Typography variant="body2">{client.bc_email}</Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2">
                            {client.oc_first_name ? `${client.oc_first_name} ${client.oc_last_name}` : t('commerce.modal.noData')}
                          </Typography>
                          <Typography variant="body2">{client.oc_email || t('commerce.modal.noData')}</Typography>
                        </>
                      )}
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                        {t('commerce.modal.businessPartner')}:
                      </Typography>
                      <Typography variant="body2">{t('commerce.modal.partnerName')}</Typography>
                      <Typography variant="body2">{t('commerce.modal.partnerEmail')}</Typography>
                    </Box>

                    <Typography sx={{ mt: 2 }} variant="subtitle2">
                      {t('commerce.modal.creationDate')}:{' '}
                      <Typography component="span" variant="body2">
                        {formatDate(client.created_at)}
                      </Typography>
                    </Typography>
                    <Typography variant="subtitle2">
                      {t('commerce.modal.modificationDate')}:{' '}
                      <Typography component="span" variant="body2">
                        {formatDate(client.updated_at)}
                      </Typography>
                    </Typography>
                  </Box>

                  {/* Column 2 */}
                  <Box sx={{ flex: 1 }}>
                    <Typography gutterBottom variant="subtitle2">
                      {t('commerce.inPersonVisit')}:{' '}
                      <Typography component="span" variant="body2">
                        {client.stage !== 'prospect' ? 'Realizada' : 'Pendiente'}
                      </Typography>
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                        {t('commerce.modal.address')}:
                      </Typography>
                      {client.address ? (
                        <>
                          <Typography variant="body2">{client.address.address_line_1}</Typography>
                          <Typography variant="body2">{client.address.city}</Typography>
                          {/* Assuming 'state' field exists or mapping it if available, user mentioned it should be added */}
                          <Typography variant="body2">{client.address.state || ''}</Typography>
                          <Typography variant="body2">{client.address.postal_code}</Typography>
                        </>
                      ) : (
                        <Typography variant="body2">{t('commerce.modal.noData')}</Typography>
                      )}
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                        {t('commerce.modal.taxInfo')}:
                      </Typography>
                      {client.tax_info ? (
                        <>
                          <Typography variant="body2">{client.tax_info.tax_name}</Typography>
                          <Typography variant="body2">{client.tax_info.rfc}</Typography>
                          <Typography variant="body2">{client.tax_info.postal_code}</Typography>
                          {/* Estado again if relevant here as per user mockup */}
                          <Typography variant="body2">{client.address?.state || 'Estado de México'}</Typography>
                        </>
                      ) : (
                        <Typography variant="body2">{t('commerce.modal.noData')}</Typography>
                      )}
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                        {t('license')}:
                      </Typography>
                      <Typography variant="body2">{client.licenses?.[0]?.license_id || t('commerce.modal.noData')}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            {activeTab === 1 && (
              <Box>
                {/* Plan Details */}
                <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 'bold' }} variant="subtitle1">
                      {t('commerce.plan')}
                    </Typography>
                    <Typography variant="body1">{client.quotes?.[0]?.plan?.name || client.quotes?.[0]?.plan_name || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2">
                      {t('commerce.numberOfAccessPoints')}: <strong>{client.quotes?.[0]?.number_of_access_points || '-'}</strong>
                    </Typography>
                    <Typography variant="body2">
                      {t('commerce.numberOfUsers')}: <strong>{client.quotes?.[0]?.number_of_users || '-'}</strong>
                    </Typography>
                    <Typography variant="body2">
                      {t('commerce.numberOfBridges')}: <strong>{client.quotes?.[0]?.number_of_bridges || '-'}</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                    <Typography sx={{ fontWeight: 'bold' }} variant="body1">
                      {t('commerce.pricePerUser')}: ${client.quotes?.[0]?.price_per_user || '-'}
                    </Typography>
                    <Typography sx={{ fontWeight: 'bold' }} variant="h6">
                      {t('commerce.totalPrice')}: ${client.quotes?.[0]?.total_price || '-'}
                    </Typography>
                  </Box>
                </Box>

                {/* Bridges Table */}
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    mt: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: theme.palette.action.hover,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      display: 'grid',
                      gridTemplateColumns: '1fr 1.5fr 1.5fr 1fr 1fr 1.2fr',
                      p: 1.5
                    }}
                  >
                    {['serialNumber', 'macAddress', 'bridgeKey', 'bridgeIV', 'name', 'creationDate'].map(header => (
                      <Typography key={header} sx={{ fontWeight: 'bold' }} variant="subtitle2">
                        {t(`commerce.modal.bridgeTable.${header}`)}
                      </Typography>
                    ))}
                  </Box>

                  {client.licenses?.[0]?.bridges?.map((bridge, index) => (
                    <Box
                      key={bridge.id || index}
                      sx={{
                        '&:last-child': { borderBottom: 'none' },
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: 'grid',
                        gridTemplateColumns: '1fr 1.5fr 1.5fr 1fr 1fr 1.2fr',
                        p: 1.5
                      }}
                    >
                      <Typography sx={{ textAlign: bridge.serial_number ? 'left' : 'center' }} variant="body2">
                        {bridge.serial_number || '-'}
                      </Typography>
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textAlign: bridge.mac_address ? 'left' : 'center',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={bridge.mac_address}
                        variant="body2"
                      >
                        {bridge.mac_address || '-'}
                      </Typography>
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textAlign: bridge.bridge_key ? 'left' : 'center',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={bridge.bridge_key}
                        variant="body2"
                      >
                        {bridge.bridge_key || '-'}
                      </Typography>
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textAlign: bridge.bridge_iv ? 'left' : 'center',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={bridge.bridge_iv}
                        variant="body2"
                      >
                        {bridge.bridge_iv || '-'}
                      </Typography>
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textAlign: bridge.name ? 'left' : 'center',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={bridge.name}
                        variant="body2"
                      >
                        {bridge.name || '-'}
                      </Typography>
                      <Typography variant="body2">{formatDate(bridge.created_at)}</Typography>
                    </Box>
                  ))}
                  {(!client.licenses?.[0]?.bridges || client.licenses[0].bridges.length === 0) && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography color="textSecondary" variant="body2">
                        {t('commerce.modal.noData')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default ClientDetailsModal;
